








































import {Component, Prop, Vue} from "vue-property-decorator";
import AgoraRTC, {ILocalAudioTrack, ILocalVideoTrack} from "agora-rtc-sdk-ng";
import DeviceTestVideo from "@share/web-conferences/components/devices-test/DeviceTestVideo.vue";
import DeviceTestAudio from "@share/web-conferences/components/devices-test/DeviceTestAudio.vue";
import DeviceSelector from "@share/web-conferences/components/devices-test/DeviceSelector.vue";
import {Debounce} from "vue-debounce-decorator";
import {DeviceApplyParams} from "@share/web-conferences/types/DeviceApplyParams";
import {releaseTrack} from "@share/web-conferences/helpers/TracksHelper";
import ControlPanelSwitcher from "@share/web-conferences/components/control-panel/ControlPanelSwitcher.vue";
import MicIcon from '../../../../assets/svg/mic.svg'
import CameraIcon from '../../../../assets/svg/wc-camera.svg'
import {getDevices} from "@share/web-conferences/helpers/DevicesHelper";
import { getDevicesFromStorage, setDevicesToStorage } from "@share/web-conferences/helpers/LocalStorageHelper";
import { DeviceTestState } from "@share/web-conferences/types/DeviceTestState";
import { setMicIsOn, setSelectedAudioDevice, setSelectedVideoDevice, setVideoIsOn } from "@share/web-conferences/store/actions";

@Component({
    name: "DevicesTest",
    components: {ControlPanelSwitcher, DeviceSelector, DeviceTestAudio, DeviceTestVideo, MicIcon, CameraIcon}
})
export default class DevicesTest extends Vue {
    @Prop({ default: false }) showDecline: boolean;
    @Prop({ default: true }) showJoin: boolean;
    @Prop({ default: false }) needBackgroundSelect: boolean;

    audioDevices: MediaDeviceInfo[] = [];
    videoDevices: MediaDeviceInfo[] = [];

    videoTrack: ILocalVideoTrack | null = null;
    audioTrack: ILocalAudioTrack | null = null;

    showDeviceSelector = false;
    isDestroyed = false;

    async mounted() {
        [this.videoDevices, this.audioDevices] = await getDevices();

        this.micIsOn = this.deviceTestState.deviceState.micIsOn;
        this.videoIsOn = this.deviceTestState.deviceState.videoIsOn;

        const fromLocalStorage = getDevicesFromStorage();

        if(!this.selectedAudioDeviceId) {
            if (fromLocalStorage.audioDeviceId && this.isDeviceInTheList(this.audioDevices, fromLocalStorage.audioDeviceId)) {
                this.selectedAudioDeviceId = fromLocalStorage.audioDeviceId;
            }
            else if (this.audioDevices && this.audioDevices.length) {
                this.selectedAudioDeviceId = this.audioDevices[0].deviceId;
            }
        }

        if(!this.selectedVideoDeviceId) {
            if (fromLocalStorage.videoDeviceId && this.isDeviceInTheList(this.videoDevices, fromLocalStorage.videoDeviceId)) {
                this.selectedVideoDeviceId = fromLocalStorage.videoDeviceId;
            }
            else if (this.videoDevices && this.videoDevices.length) {
                this.selectedVideoDeviceId = this.videoDevices[0].deviceId;
            }
        }

        await this.initVideoTrack();
        await this.initAudioTrack();

        if (this.videoTrack != null && this.audioTrack != null) {
            this.$emit('tracksAcquired');
        }

        navigator.mediaDevices.addEventListener('devicechange', this.onDeviceChange);

        if (this.isDestroyed) {
            this.onDestroy();
        }
    }

    async initVideoTrack() {
        try {
            this.videoTrack = await AgoraRTC.createCameraVideoTrack({cameraId: this.selectedVideoDeviceId});
        } catch (e) {
            this.videoTrack = null;
        }
    }

    async initAudioTrack() {
        try {
            this.audioTrack = await AgoraRTC.createMicrophoneAudioTrack({microphoneId: this.selectedAudioDeviceId});
        } catch (e) {
            this.audioTrack = null;
        }
    }

    isDeviceInTheList(list: MediaDeviceInfo[], deviceId: string) {
        return list.findIndex(item => item.deviceId == deviceId) >= 0;
    }

    onShowDeviceSelector() {
        this.showDeviceSelector = true;
    }

    onShowBackgroundSelect() {
        this.$emit("showBackgroundSelect");
    }

    @Debounce(300)
    async onDeviceChange() {
        [this.videoDevices, this.audioDevices] = await getDevices();
    }

    onClose() {
        this.showDeviceSelector = false;
    }

    onApply(params: DeviceApplyParams) {
        if (params.videoDeviceId) {
            this.selectedVideoDeviceId = params.videoDeviceId;
        }
        if (params.audioDeviceId) {
            this.selectedAudioDeviceId = params.audioDeviceId;
        }

        setDevicesToStorage({
            audioDeviceId: this.selectedAudioDeviceId,
            videoDeviceId: this.selectedVideoDeviceId,
        });

        if (params.videoTrack) {
            releaseTrack(this.videoTrack);
            this.videoTrack = params.videoTrack;
        }
        if (params.audioTrack) {
            releaseTrack(this.audioTrack);
            this.audioTrack = params.audioTrack;
        }

        this.$emit('changeDevice')

        this.onClose();
    }

    onJoin() {
        const data = {
            audioDeviceId: this.selectedAudioDeviceId,
            videoDeviceId: this.selectedVideoDeviceId,
            micIsOn: this.micIsOn,
            videoIsOn: this.videoIsOn
        };

        this.$emit('join', data);
    }

    onVideoOnChange() {
        this.$nextTick(() => {
            this.videoIsOn ? (this.$refs?.video as DeviceTestVideo)?.playVideo() : (this.$refs?.video as DeviceTestVideo)?.stopVideo();
        })
    }

    onDestroy() {
        this.videoTrack && releaseTrack(this.videoTrack);
        this.audioTrack && releaseTrack(this.audioTrack);
        navigator.mediaDevices.removeEventListener('devicechange', this.onDeviceChange);
    }

    destroyed() {
        this.onDestroy();
        this.isDestroyed = true;
    }

    get selectedAudioDeviceId() {
        return this.deviceTestState.selectedDevices.audioDeviceId
    }

    set selectedAudioDeviceId(audiDeviceId: string) {
        this.$store.dispatch(setSelectedAudioDevice, audiDeviceId);
    }

    get selectedVideoDeviceId() {
        return this.deviceTestState.selectedDevices.videoDeviceId
    }

    set selectedVideoDeviceId(videoDeviceId: string) {
        this.$store.dispatch(setSelectedVideoDevice, videoDeviceId);
    }

    get micIsOn() {
        return this.deviceTestState.deviceState.micIsOn
    }

    set micIsOn(micIsOn: boolean) {
        this.$store.dispatch(setMicIsOn, micIsOn);
    }

    get videoIsOn() {
        return this.deviceTestState.deviceState.videoIsOn
    }

    set videoIsOn(videoIsOn: boolean) {
        this.$store.dispatch(setVideoIsOn, videoIsOn);
    }

    get deviceTestState(): DeviceTestState {
        return this.$store.state.webConference.deviceTestState;
    }
}
